import type { JSX } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { ButtonProps } from '@innovamat/glimmer-components';
import { Modal } from '@innovamat/glimmer-components';

type Props = {
  userExistsError: boolean;
  expiredLinkError: boolean;
  email: string | undefined;
  loadingReenvitation: boolean;
  isOpen: boolean;
  goToLogin: () => void;
  goToForgotPassword: () => void;
  onClose?: () => void;
  onResendLink?: () => void;
};

export default function ModalError({
  userExistsError,
  expiredLinkError,
  loadingReenvitation,
  goToForgotPassword,
  goToLogin,
  isOpen = false,
  onClose,
  onResendLink,
}: Props): JSX.Element {
  const { t } = useTranslation();

  const getTitle = (): string => {
    if (userExistsError) {
      return t('teacher.onboarding.createProfileFilled.errorExists.title');
    }

    if (expiredLinkError) {
      return t('teacher.onboarding.createProfileFilled.errorExpired.title');
    }

    return t('teacher.onboarding.createProfileFilled.error.title');
  };

  const getBody = (): JSX.Element => {
    if (userExistsError) {
      return (
        <>{t('teacher.onboarding.createProfileFilled.error.errorExists')}</>
      );
    }

    if (expiredLinkError) {
      return (
        <Trans
          i18nKey="teacher.onboarding.createProfileFilled.error.expired"
          components={{
            br: <br />,
          }}
        />
      );
    }

    return <>{t('teacher.onboarding.createProfileFilled.error.body')}</>;
  };

  const actionButtons = (): ButtonProps[] => {
    if (userExistsError) {
      return [
        {
          children: t('Iniciar sesión'),
          onClick: goToLogin,
          variant: 'accent',
        },
        {
          children: t('forgotPassword', 'He olvidado mi contraseña'),
          onClick: goToForgotPassword,
          variant: 'tertiary',
        },
      ];
    }

    if (expiredLinkError) {
      return [
        {
          children: t('teacher.onboarding.createProfileFilled.resendLink'),
          onClick: onResendLink,
          variant: 'accent',
          loading: loadingReenvitation,
        },
        {
          children: t('Settings.Cancel'),
          onClick: onClose,
          variant: 'tertiary',
        },
      ];
    }
    
    return [
      {
        children: t('teacher.onboarding.createProfileFilled.error.button'),
        onClick: onClose,
        variant: 'accent',
      },
    ];
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnClickOutside={!userExistsError && !expiredLinkError}
      title={getTitle()}
      modalWidth="650px"
      closeButton="inner"
      buttons={actionButtons()}
    >
      {getBody()}
    </Modal>
  );
}
